import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { LoaderIcon, RightLeftArrowIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useCurrentJointBillServicePeriodId } from "@hooks/useCurrentJointBillServicePeriodId";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useTransferPreviousExpectedPaymentsMutation } from "@services/jointBillServices/mutations";
import { useJointBillServicePeriodDataQuery } from "@services/overview/queries";
import { formatPrice } from "@utils/units";

interface IBillCarryOverModalProps extends IUseModalProps {
  billId: number;
  jointBillServiceAgreementId: number;
  jointBillServiceId: number;
}

export const BillCarryOverModal: React.FC<IBillCarryOverModalProps> = (
  props,
) => {
  const { jointBillServiceAgreementId, jointBillServiceId, billId, ...rest } =
    props;
  const modal = useModal(rest);
  const { t } = useTranslation();

  const currentPeriodId = useCurrentJointBillServicePeriodId();
  const { data: previousPeriod, isLoading } =
    useJointBillServicePeriodDataQuery(
      {
        jointBillServiceId,
        periodId: (currentPeriodId ?? 0) - 1,
      },
      !!currentPeriodId,
    );

  const previousPeriodBill = previousPeriod?.billData.find(
    (bill) => bill.jointBillServiceAgreementId === jointBillServiceAgreementId,
  );

  const { mutateAsync, isPending } =
    useTransferPreviousExpectedPaymentsMutation();

  const { toastApiError } = useApiErrorUtils();

  const handleConfirm = async () => {
    if (!currentPeriodId) return;

    const response = await mutateAsync({
      billId,
      jointBillServiceId,
      periodId: currentPeriodId,
    }).catch(toastApiError);

    if (response) {
      toast.success(t("billCarryOverModal.toast.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      title={t("billCarryOverModal.title")}
      subtitle={t("billCarryOverModal.subtitle")}
      badgeProps={{ icon: RightLeftArrowIcon }}
      className="md:max-w-xl"
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            isLoading={isPending}
            onClick={handleConfirm}
          >
            {t("billCarryOverModal.confirm")}
          </Button>
        </React.Fragment>
      }
    >
      {isLoading && <LoaderIcon className="animate-spin" />}
      <div className="flex items-center justify-between">
        <h4>{previousPeriodBill?.displayName ?? t("common.defaultAddress")}</h4>
        <span>{formatPrice(previousPeriodBill?.totalAmount ?? 0)}</span>
      </div>
    </Modal>
  );
};
